import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Button, DataTable, Pagination, LinkCell, Select, Modal, Textarea, useToast, formatDate, type Column,
} from '@datahub/design-system'
import { get, post, downloadCsv } from '../../lib/api'
import { entityBasePath, entityPath } from '../../lib/paths'
import { ListPage } from '../AdminShell'
import { StatusTag } from '../../components/StatusTag'

interface CycleRow {
  id: string
  company: {
    id: string; displayName: string; entityType: string
    cadence: { cadence: string } | null; owner: { displayName: string | null; email: string } | null
  }
  kind: string
  periodLabel: string
  status: string
  openedAt: string | null
  deadlineAt: string | null
  completionPct: number
  lastReminderAt: string | null
  submittedAt: string | null
  daysToSubmit: number | null
  rowCounts: Record<string, number>
}

/** Cadence tracker (RPT-04). */
export function Cycles() {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [closing, setClosing] = useState<CycleRow | null>(null)
  const [reason, setReason] = useState<'No response' | 'Not applicable' | 'Duplicate' | 'Other'>('No response')
  const [note, setNote] = useState('')

  const status = params.get('status') ?? ''
  const cadence = params.get('cadence') ?? ''
  const periodLabel = params.get('periodLabel') ?? ''

  const { data, isLoading } = useQuery({
    queryKey: ['cycles', status, cadence, periodLabel, page],
    queryFn: () =>
      get<{ total: number; items: CycleRow[] }>(
        `/admin/cycles?${new URLSearchParams({
          ...(status ? { status } : {}), ...(cadence ? { cadence } : {}), ...(periodLabel ? { periodLabel } : {}),
          page: String(page), pageSize: '25',
        })}`,
      ),
  })

  const nudge = useMutation({
    mutationFn: (company: { id: string; entityType: string }) =>
      post(`${entityBasePath(company.entityType)}/${company.id}/nudge`, {}),
    onSuccess: () => toast.success('Nudge sent'),
    onError: (e: Error) => toast.error(e.message),
  })
  const close = useMutation({
    mutationFn: () => post(`/admin/cycles/${closing!.id}/close`, { reason, note: note || undefined }),
    onSuccess: () => { toast.success('Cycle closed'); setClosing(null); setNote(''); void queryClient.invalidateQueries({ queryKey: ['cycles'] }) },
    onError: (e: Error) => toast.error(e.message),
  })

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
    setPage(1)
  }

  const columns: Column<CycleRow>[] = [
    { key: 'company', header: 'Company', width: 240, sticky: true,
      render: (c) => <LinkCell onClick={() => navigate(entityPath(c.company.entityType, c.company.id))}>{c.company.displayName}</LinkCell> },
    { key: 'owner', header: 'Owner', width: 170, render: (c) => c.company.owner?.displayName ?? c.company.owner?.email ?? '-' },
    { key: 'period', header: 'Period', width: 140, render: (c) => c.periodLabel },
    { key: 'status', header: 'Status', width: 140, render: (c) => <StatusTag status={c.status} /> },
    { key: 'opened', header: 'Invited', width: 130, render: (c) => formatDate(c.openedAt) },
    { key: 'reminder', header: 'Last reminder', width: 140, render: (c) => formatDate(c.lastReminderAt) },
    { key: 'submitted', header: 'Submitted', width: 130, render: (c) => formatDate(c.submittedAt) },
    { key: 'days', header: 'Days to submit', width: 130, align: 'right', render: (c) => (c.daysToSubmit === null ? '-' : String(c.daysToSubmit)) },
    { key: 'completion', header: 'Completion', width: 110, align: 'right', render: (c) => `${c.completionPct}%` },
    { key: 'rows', header: 'Rows reported', width: 200,
      render: (c) => {
        const entries = Object.entries(c.rowCounts ?? {})
        return entries.length === 0 ? '-' : entries.map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(', ')
      } },
    { key: 'actions', header: '', width: 170,
      render: (c) => (
        <span className="flex items-center" style={{ gap: 12 }}>
          <button onClick={() => nudge.mutate(c.company)} className="text-text-hyperlink hover:underline" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}>Nudge</button>
          <button onClick={() => setClosing(c)} className="text-text-support hover:text-white" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}>Close</button>
        </span>
      ) },
  ]

  return (
    <ListPage
      title="Cycles"
      actions={<Button variant="outline" size="medium" onClick={() => void downloadCsv(`/admin/exports/tracker?${params}`, 'datahub-tracker.csv')}>Export</Button>}
    >
      <div className="flex items-center flex-shrink-0 bg-background-main" style={{ height: 54, padding: '0 20px', gap: 12, borderBottom: '0.5px solid #4B5563' }}>
        <div style={{ width: 180 }}>
          <Select size="small" ariaLabel="Filter by cycle status" placeholder="All statuses" value={status} onChange={(v) => setFilter('status', v)}
            options={[{ value: '', label: 'All statuses' }, ...['scheduled', 'open', 'overdue', 'submitted', 'amended', 'closed'].map((s) => ({ value: s, label: s }))]} />
        </div>
        <div style={{ width: 180 }}>
          <Select size="small" ariaLabel="Filter by cadence" placeholder="All cadences" value={cadence} onChange={(v) => setFilter('cadence', v)}
            options={[{ value: '', label: 'All cadences' }, ...['monthly', 'quarterly', 'semi_annual', 'annual'].map((s) => ({ value: s, label: s }))]} />
        </div>
      </div>

      <div className="flex-1" style={{ overflow: 'hidden' }}>
        {isLoading
          ? <p className="font-mulish text-text-support" style={{ fontSize: 12, padding: 20 }}>Loading…</p>
          : <DataTable columns={columns} rows={data?.items ?? []} rowKey={(c) => c.id} caption="Reporting cycles" emptyMessage="No cycles match those filters" maxHeight="100%" />}
      </div>
      <Pagination page={page} pageSize={25} total={data?.total ?? 0} onPageChange={setPage} />

      <Modal
        open={closing !== null}
        onClose={() => setClosing(null)}
        title={`Close ${closing?.periodLabel ?? ''} for ${closing?.company.displayName ?? ''}`}
        primaryLabel="Close cycle"
        primaryLoading={close.isPending}
        onPrimary={() => close.mutate()}
      >
        <div className="flex flex-col" style={{ gap: 20 }}>
          <Select
            label="Reason" size="large" value={reason} onChange={(v) => setReason(v as typeof reason)}
            options={['No response', 'Not applicable', 'Duplicate', 'Other'].map((r) => ({ value: r, label: r }))}
          />
          <Textarea label="Note (optional)" value={note} onChange={setNote} rows={3} maxLength={500} />
        </div>
      </Modal>
    </ListPage>
  )
}
